import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Legend,
} from 'recharts';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';

/**
 * Bar chart showing the monthly evolution of the cumulative profit and the
 * estimated annual IS over the last 12 months. The vertical reference line
 * at 42 500 € of bénéfice annualisé highlights the IS bracket jump from
 * 15 % to 25 % — the apprenant sees instantly when their taxation regime
 * is about to change.
 *
 * Source : `state.monthlyProfitHistory` populated by taxSlice.checkTaxDeadlines
 * at each month close. If the array is empty (early game), we render a
 * friendly placeholder so the panel doesn't look broken.
 */
const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function ProfitHistoryChart() {
  const history = useGameStore(s => s.monthlyProfitHistory || EMPTY_ARRAY);
  const annualProfit = useGameStore(s => s.annualProfit || 0);
  const month = useGameStore(s => s.month || 1);
  const year = useGameStore(s => s.year || 1);

  // Append the current (in-progress) month as the rightmost bar so the user
  // sees their live position alongside the historical context.
  // 12/02/2026 — Lissage début d'année via `projectAnnualProfit`.
  const { annualIS: liveIs } = projectAnnualProfit({ annualProfit, month });
  const data = [
    ...history.map(h => ({
      label: `${MONTH_LABELS[(h.month || 1) - 1]} ${String(h.year || 1).slice(-2)}`,
      profitCumul: h.profitCumul,
      isEstAnnual: h.isEstAnnual,
    })),
    {
      label: `${MONTH_LABELS[month - 1]} ${String(year).slice(-2)} (en cours)`,
      profitCumul: Math.round(annualProfit),
      isEstAnnual: Math.round(liveIs),
    },
  ];

  if (data.length === 1 && annualProfit === 0) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-ink-600 p-6 text-center text-sm text-slate-500"
        data-testid="profit-history-empty"
      >
        Données insuffisantes — le graphique apparaîtra dès la fin du 1er mois.
      </div>
    );
  }

  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="profit-history-chart">
      <div className="flex items-baseline justify-between mb-2">
        <p className="text-sm font-semibold text-slate-200">📈 Évolution du bénéfice & de l'IS estimé</p>
        <p className="text-[10px] text-slate-500">12 derniers mois</p>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
          <XAxis dataKey="label" stroke="#9CA3AF" tick={{ fontSize: 10 }} />
          <YAxis stroke="#9CA3AF" tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <Tooltip
            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 12 }}
            formatter={(value, name) => [`${Math.round(value).toLocaleString('fr-FR')} €`, name]}
            labelStyle={{ color: '#cbd5e1', fontWeight: 600 }}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
          {/* IS bracket-jump reference at 42 500 € — bénéfice annualisé crossing
              this threshold flips the tax rate from 15 % to 25 %. */}
          <ReferenceLine
            y={42500}
            stroke="#F59E0B"
            strokeDasharray="4 4"
            label={{ value: 'Seuil 15→25 %', fontSize: 9, fill: '#F59E0B', position: 'insideTopRight' }}
          />
          <Bar dataKey="profitCumul"  name="Bénéfice cumulé"  fill="#0EA5E9" radius={[4, 4, 0, 0]} />
          <Bar dataKey="isEstAnnual"  name="IS annuel estimé" fill="#F59E0B" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
        La ligne pointillée jaune marque le seuil <strong className="text-amber-300">42 500 €</strong> au-delà
        duquel le bénéfice est imposé à <strong>25 %</strong> (au lieu de 15 %). Anticipez les investissements
        avant la fin de l'exercice pour rester en zone basse.
      </p>
    </div>
  );
}
